// <ex-viewer> — Extreme card in 3D: STL body + DXF-derived engraving decals + HTML tool markers.
// Reads tools from window.EX (blocks file), selection from window.__exactive, reports clicks via window.__exselect(n).
(function () {
  const THREE_URL = 'https://unpkg.com/three@0.184.0/build/three.module.js';
  const W = 87.39, H = 55.2, T = 3;          // card mm, straight off the STL bbox
  const FZ = -1;                             // z-sign of the FRONT (metric) face after the geometry is stood up.
                                             // Coverage test on the STL silhouette: face-A artwork lands 0.889 against the
                                             // MIRRORED raster vs 0.508 direct (B: 0.877 direct vs 0.512) ⇒ face A lives at -z.
  const nz = (side) => (side === 'A' ? FZ : -FZ);
  // a tool's spot on its face, in card mm — a hand-placed mark wins over the authored guess
  function spot(t, mark) {
    if (mark && mark.mx != null) return { mx: mark.mx, my: mark.my, side: mark.side, mine: true };
    const CM = window.CardMap;
    const mm = CM ? CM.mmOf('ex', t) : { mx: 0, my: 0 };
    return { mx: mm.mx, my: mm.my, side: t.side, mine: !t.est };
  }

  function parseSTL(buf) {
    const dv = new DataView(buf);
    const tri = dv.getUint32(80, true);
    if (84 + tri * 50 !== buf.byteLength) throw new Error('only binary STL supported');
    const pos = new Float32Array(tri * 9), nor = new Float32Array(tri * 9);
    for (let i = 0; i < tri; i++) {
      const o = 84 + i * 50;
      const nx = dv.getFloat32(o, true), ny = dv.getFloat32(o + 4, true), nz = dv.getFloat32(o + 8, true);
      for (let v = 0; v < 3; v++) {
        const p = o + 12 + v * 12, k = i * 9 + v * 3;
        pos[k] = dv.getFloat32(p, true); pos[k + 1] = dv.getFloat32(p + 4, true); pos[k + 2] = dv.getFloat32(p + 8, true);
        nor[k] = nx; nor[k + 1] = ny; nor[k + 2] = nz;
      }
    }
    return { pos, nor, tri };
  }

  class EXViewer extends HTMLElement {
    connectedCallback() {
      if (this._up) { this._revive(); return; }
      this._up = true;
      this.style.cssText = 'position:relative; display:block; width:100%; height:100%; min-height:280px; background:radial-gradient(120% 100% at 50% 42%, #262C33 0%, #1A1E24 58%, #11141A 100%); touch-action:none;';
      this.innerHTML =
        '<canvas style="display:block; width:100%; height:100%; cursor:grab;"></canvas>' +
        '<svg data-lead style="position:absolute; inset:0; width:100%; height:100%; pointer-events:none; overflow:visible;"><line stroke="#FF6B2C" stroke-width="1.5" stroke-dasharray="4 3" opacity="0"></line><circle r="3" fill="#FF6B2C" opacity="0"></circle></svg>' +
        '<div data-labels style="position:absolute; inset:0; pointer-events:none;"></div>' +
        '<div data-hint style="position:absolute; left:12px; bottom:12px; font-family:\'IBM Plex Mono\',monospace; font-size:11px; letter-spacing:0.1em; color:rgba(236,237,238,0.72); background:rgba(11,12,14,0.72); padding:4px 8px;">LOADING 3D…</div>' +
        '<div data-btns style="position:absolute; right:12px; top:12px; display:flex; gap:6px;"></div>';
      // a remount leaves a stale RAF id behind, so liveness is judged by heartbeat, never by the id
      this._revive = () => {
        if (!this._tickFn || !this.isConnected) return;
        if (this._beat != null && performance.now() - this._beat < 250) return;
        if (this._raf != null) cancelAnimationFrame(this._raf);
        this._raf = null;
        this._tickFn();
      };
      this._watchdog = setInterval(() => {
        if (this.isConnected && this.clientWidth > 0 && this._tickFn && (this._beat == null || performance.now() - this._beat > 400)) { this._revive(); if (this.refresh && (this._beat == null || performance.now() - this._beat > 400)) this.refresh(); }
      }, 500);
      this.canvas = this.querySelector('canvas');
      this.labels = this.querySelector('[data-labels]');
      this.lead = this.querySelector('[data-lead] line');
      this.leadDot = this.querySelector('[data-lead] circle');
      this.hint = this.querySelector('[data-hint]');
      const mkBtn = (label, fn) => {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = "cursor:pointer; font-family:'IBM Plex Mono',monospace; font-size:11px; letter-spacing:0.06em; color:#ECEDEE; background:rgba(11,12,14,0.78); border:1px solid #33383F; border-radius:9px; padding:7px 10px;";
        b.onmouseenter = () => { b.style.borderColor = '#FF6B2C'; };
        b.onmouseleave = () => { b.style.borderColor = '#33383F'; };
        b.onclick = fn;
        this.querySelector('[data-btns]').appendChild(b);
        return b;
      };
      this._mkBtn = mkBtn;
      this.boot().catch(e => { this.hint.textContent = '3D FAILED: ' + e.message; console.error('ex-viewer', e); });
    }

    async boot() {
      const THREE = await import(THREE_URL);
      this.THREE = THREE;
      const renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(38, 1, 1, 3000);
      scene.add(new THREE.HemisphereLight(0xf2f4f7, 0x4a5059, 0.55));
      const key = new THREE.DirectionalLight(0xffffff, 1.0); key.position.set(70, 130, 150); scene.add(key);
      const rim = new THREE.DirectionalLight(0xcfe0f5, 1.05); rim.position.set(-90, -50, -130); scene.add(rim);
      const fill = new THREE.DirectionalLight(0xffffff, 0.5); fill.position.set(-40, 90, -60); scene.add(fill);

      // studio环境 — metals read as black without an env map
      (() => {
        const c = document.createElement('canvas'); c.width = 512; c.height = 256;
        const x = c.getContext('2d');
        const gr = x.createLinearGradient(0, 0, 0, 256);
        gr.addColorStop(0, '#eef2f7'); gr.addColorStop(0.5, '#9aa3ad'); gr.addColorStop(1, '#2a2e33');
        x.fillStyle = gr; x.fillRect(0, 0, 512, 256);
        x.fillStyle = 'rgba(255,255,255,0.95)'; x.fillRect(60, 30, 150, 60);
        x.fillStyle = 'rgba(210,225,245,0.7)'; x.fillRect(330, 60, 90, 90);
        const tex = new THREE.CanvasTexture(c);
        tex.mapping = THREE.EquirectangularReflectionMapping;
        const pm = new THREE.PMREMGenerator(renderer);
        scene.environment = pm.fromEquirectangular(tex).texture;
        tex.dispose(); pm.dispose();
      })();
      const black = new THREE.MeshStandardMaterial({ name: 'pvd-black', color: 0x17191d, metalness: 0.55, roughness: 0.42, envMapIntensity: 0.7 });
      const ti = new THREE.MeshStandardMaterial({ name: 'titanium', color: 0xc6ccd3, metalness: 0.84, roughness: 0.36, envMapIntensity: 1.35 });
      this.mats = { black, ti };

      const { pos, nor, tri } = parseSTL(await (await fetch('https://mrf.tools/cdn/shop/files/mrf26-ex.stl?v=b3')).arrayBuffer());
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
      g.computeBoundingBox();
      const size = new THREE.Vector3(); g.boundingBox.getSize(size);
      const scale = W / Math.max(size.x, size.y, size.z);   // longest axis is the 85 mm width
      g.scale(scale, scale, scale);
      g.computeBoundingBox();
      const ctr = new THREE.Vector3(); g.boundingBox.getCenter(ctr);
      g.translate(-ctr.x, -ctr.y, -ctr.z);
      g.getAttribute('normal');
      // card came in as XZ plane with thickness on Y — stand it up: thickness on Z
      g.rotateX(-Math.PI / 2);
      g.computeVertexNormals();
      const card = new THREE.Mesh(g, black);
      card.name = 'extreme';
      scene.add(card);
      this.card = card;

      const loader = new THREE.TextureLoader();
      const decal = (file, sign) => {
        const tex = loader.load(file, () => { this._dirty = true; });
        tex.anisotropy = 8;
        // the texture is punched by the card's own silhouette, so no engraving hangs over a cutout
        // or past an edge (that spill made the two faces read as one mixed-up marking)
        const m = new THREE.Mesh(new THREE.PlaneGeometry(W, H), new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.96, depthWrite: false }));
        m.position.z = sign * (T / 2 + 0.02);
        if (sign < 0) m.rotation.y = Math.PI;
        m.name = 'marking-' + (sign > 0 ? 'a' : 'b');
        scene.add(m);
        return m;
      };
      this.decals = [decal('https://mrf.tools/cdn/shop/files/mrf26-ex-decal-a.png?v=b3', nz('A')), decal('https://mrf.tools/cdn/shop/files/mrf26-ex-decal-b.png?v=b3', nz('B'))];

      // orbit (hand-rolled: no import-map dependency)
      const FRONT_T = (FZ > 0 ? 0 : Math.PI) + 0.35;   // open on the FRONT (logo) face, whichever z-sign it sits on
      let theta = FRONT_T, phi = 1.25, r = 165, drag = null, auto = true, home = FRONT_T, sway = 0;
      this._fit = () => {
        const w = this.clientWidth || 1, h = this.clientHeight || 1;
        const vFov = camera.fov * Math.PI / 180;
        const need = Math.max(H / 2 / Math.tan(vFov / 2), W / 2 / Math.tan(vFov / 2) / (w / h));
        this._base = need * 1.22;
        // in the work position the handle reaches ~40 mm past the card edge
        if (!this._zoomed) r = this._base * (this._wide ? 1.5 : 1);
      };
      this._fit();
      const place = () => {
        camera.position.set(r * Math.sin(phi) * Math.sin(theta), r * Math.cos(phi), r * Math.sin(phi) * Math.cos(theta));
        camera.lookAt(0, 0, 0);
      };
      // screen point -> mm on whichever face is turned towards the camera (placement mode)
      this._hit = (cx, cy) => {
        const rect = this.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height || Math.abs(camera.position.z) < 6) return null;
        const side = (camera.position.z > 0) === (FZ > 0) ? 'A' : 'B';
        const v3 = new THREE.Vector3(((cx - rect.left) / rect.width) * 2 - 1, -(((cy - rect.top) / rect.height) * 2 - 1), 0.5);
        v3.unproject(camera);
        const dir = v3.sub(camera.position);
        if (Math.abs(dir.z) < 1e-6) return null;
        const k = (nz(side) * (T / 2) - camera.position.z) / dir.z;
        if (k < 0) return null;
        const wx = camera.position.x + dir.x * k, wy = camera.position.y + dir.y * k;
        const mx = nz(side) > 0 ? wx + W / 2 : W / 2 - wx;
        return { side: side, mx: Math.max(0, Math.min(W, mx)), my: Math.max(0, Math.min(H, H / 2 - wy)) };
      };
      this._dropAt = (cx, cy, n, advance) => {
        const h = this._hit(cx, cy);
        if (!h) return;
        // a live drag must not rebuild the label (that would release pointer capture): move this one in place
        const mk = (this.markers || []).find(m => m.n === n);
        if (mk) {
          const s2 = nz(h.side), MW = W, MH = H, MT = T;
          mk.side = h.side;
          mk.p = [s2 > 0 ? h.mx - MW / 2 : MW / 2 - h.mx, MH / 2 - h.my, s2 * (MT / 2 + 0.2)];
          mk.el.dataset.solid = '1';
          this._dirty = true;
        }
        if (window.__explace) window.__explace(n, h.side, h.mx, h.my, advance);
      };

      const pts = new Map();
      let pinch = 0;
      const spread = () => { const a = [...pts.values()]; return Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y); };
      this.canvas.addEventListener('pointerdown', (e) => {
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        auto = false; this._touched = true;
        this._moved = 0; this._multi = pts.size >= 2;
        if (pts.size === 2) { pinch = spread(); drag = null; }
        else { drag = { x: e.clientX, y: e.clientY }; this.canvas.style.cursor = 'grabbing'; }
        try { this.canvas.setPointerCapture(e.pointerId); } catch (err) {}
      });
      this.canvas.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size >= 2) {   // two fingers: pinch to zoom, no spin
          this._multi = true;
          const d = spread();
          if (pinch > 0 && d > 0) r = Math.min(420, Math.max(95, r * (pinch / d)));
          pinch = d;
          return;
        }
        if (!drag) return;
        this._moved += Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y);
        theta -= (e.clientX - drag.x) * 0.008;
        phi = Math.min(Math.PI - 0.12, Math.max(0.12, phi - (e.clientY - drag.y) * 0.008));
        drag = { x: e.clientX, y: e.clientY };
      });
      const stop = (e) => {
        pts.delete(e.pointerId);
        if (pts.size < 2) pinch = 0;
        if (pts.size === 1) { const a = [...pts.values()][0]; drag = { x: a.x, y: a.y }; }
        if (pts.size === 0) { drag = null; this.canvas.style.cursor = window.__excal ? 'crosshair' : 'grab'; }
      };
      this.canvas.addEventListener('pointerup', (e) => {
        // a tap on the card in placement mode drops the armed marker; a real drag still spins
        const n = window.__excal;
        if (n && !this._multi && this._moved < 7 && pts.size <= 1) this._dropAt(e.clientX, e.clientY, n, true);
      });
      this.canvas.addEventListener('pointerup', stop);
      this.canvas.addEventListener('pointercancel', stop);
      // screen-wide pinch (Tool Explorer forwards two-finger gestures from anywhere on the page)
      this.zoomBy = (f) => { this._touched = true; this._zoomed = true; auto = false; this._fly = null; r = Math.min(420, Math.max(95, r * f)); this._dirty = true; };
      this.cancelDrag = () => { drag = null; pts.clear(); pinch = 0; };
      this.canvas.addEventListener('wheel', (e) => { e.preventDefault(); this._touched = true; r = Math.min(420, Math.max(95, r + e.deltaY * 0.25)); }, { passive: false });

      let fly = null, pt = null, pp = null, pr = null;
      this._dirty = true;
      this.flyTo = (side) => { home = (nz(side) > 0 ? 0 : Math.PI) + 0.35; fly = { theta: home, phi: 1.25 }; auto = false; };
      // first paint: land on the face the pre-selected tool sits on, without killing the idle spin
      this._snapTo = (side) => { home = (nz(side) > 0 ? 0 : Math.PI) + 0.35; theta = home; phi = 1.25; };
      this.canvas.addEventListener('pointerdown', () => { fly = null; }, true);
      this._mkBtn('FRONT', () => this.flyTo('A'));
      this._mkBtn('BACK', () => this.flyTo('B'));
      let finish = 'black';
      const fb = this._mkBtn('NATURAL', () => {
        finish = finish === 'black' ? 'ti' : 'black';
        card.material = finish === 'black' ? black : ti;
        // engraving reads white on black PVD, dark on bare titanium
        this.decals.forEach(d => d.material.color.set(finish === 'black' ? 0xffffff : 0x17191c));
        fb.textContent = finish === 'black' ? 'NATURAL' : 'BLACK';
        this._dirty = true;
      });

// ---- handle assembly (from the CAD assy): orange anodised torque grip, shown stowed on the card or extended for work
      const hOrange = new THREE.MeshStandardMaterial({ name: 'handle-orange', color: 0xe2542a, metalness: 0.2, roughness: 0.62, envMapIntensity: 0.85 });
      const hTi = new THREE.MeshStandardMaterial({ name: 'handle-titanium', color: 0xC9CFD6, metalness: 0.88, roughness: 0.32, envMapIntensity: 1.4 });
      const grip = new THREE.Group();
      grip.name = 'handle';
      grip.visible = false;
      scene.add(grip);
      this.grip = grip;
      // handle STLs live in the card's own CAD frame, so they take the CARD's scale and centre offset
      const mkGrip = (buf, name, parent) => {
        const p = parseSTL(buf);
        const gg = new THREE.BufferGeometry();
        gg.setAttribute('position', new THREE.BufferAttribute(p.pos, 3));
        gg.scale(scale, scale, scale);
        gg.translate(-ctr.x, -ctr.y, -ctr.z);
        gg.rotateX(-Math.PI / 2);
        gg.computeVertexNormals();
        const m = new THREE.Mesh(gg, hOrange);
        m.name = name;
        (parent || grip).add(m);
        return m;
      };
      const gWork = new THREE.Group(); gWork.name = 'handle-work';
      const gComp = new THREE.Group(); gComp.name = 'handle-compact';
      grip.add(gWork); grip.add(gComp);
      let pos2 = 'compact';
      const paintGrip = () => {
        gWork.visible = pos2 === 'work';
        gComp.visible = pos2 === 'compact';
        [cb2, wb2].forEach(b => { const on = (b === cb2) === (pos2 === 'compact');
          b.dataset.on = on ? '1' : ''; b.style.borderColor = on ? '#FF6B2C' : '#33383F'; b.style.color = on ? '#FF6B2C' : '#ECEDEE';
          b.style.opacity = grip.visible ? '1' : '0.4'; });
        gb.textContent = grip.visible ? '− HANDLE' : '+ HANDLE';
        gb.dataset.on = grip.visible ? '1' : '';
        gb.style.borderColor = grip.visible ? '#FF6B2C' : '#33383F';
        gb.style.color = grip.visible ? '#FF6B2C' : '#ECEDEE';
        this._wide = grip.visible && pos2 === 'work';
        this._fit();
        this._dirty = true;
      };
      const gb = this._mkBtn('+ HANDLE', () => { grip.visible = !grip.visible; auto = false; paintGrip(); });
      const cb2 = this._mkBtn('STOWED', () => { pos2 = 'compact'; if (!grip.visible) grip.visible = true; auto = false; paintGrip(); });
      const wb2 = this._mkBtn('IN WORK', () => { pos2 = 'work'; if (!grip.visible) grip.visible = true; auto = false; paintGrip(); });
      Promise.all(['https://mrf.tools/cdn/shop/files/mrf26-ex-handle.stl?v=b3', 'https://mrf.tools/cdn/shop/files/mrf26-ex-handle-compact.stl?v=b3', 'https://mrf.tools/cdn/shop/files/mrf26-ex-screws.stl?v=b3'].map(u => fetch(u).then(r => r.arrayBuffer())))
        .then(([wb, cb, sb]) => {
          mkGrip(wb, 'handle-work-body', gWork);
          mkGrip(cb, 'handle-compact-body', gComp);
          mkGrip(sb, 'handle-screws-work', gWork);
          mkGrip(sb.slice(0), 'handle-screws-compact', gComp);
          paintGrip();
        })
        .catch(e => console.warn('ex handle load', e));
      paintGrip();
            // only FRONT/BACK stay on the stage; every other control moves to an in-flow bar above it (hub #finishRow),
      // same pattern as the Designer/Professional colour strips — overlays were covering the model on phones
      (() => {
        const host = document.getElementById('finishRow');
        if (!host) return;
        const bar = document.createElement('div');
        bar.setAttribute('data-finish-for', this.id || 'viewer');
        bar.setAttribute('data-actions-bar', '1');
        bar.style.cssText = 'display:flex; align-items:center; gap:8px; flex-wrap:wrap; border:1px solid #262A30; border-radius:12px; padding:8px 12px; background:#121417;';
        const lab = document.createElement('span');
        lab.textContent = 'FINISH & HANDLE';
        lab.style.cssText = "font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:0.18em; color:#FF6B2C; margin-right:4px;";
        bar.appendChild(lab);
        [fb, gb, cb2, wb2].forEach(b => { if (!b) return; b.style.background = 'transparent'; bar.appendChild(b); });
        host.appendChild(bar);
      })();
      this.hint.textContent = 'DRAG TO SPIN · PINCH OR SCROLL TO ZOOM · TAP A NUMBER';
      this.buildLabels();

      const v = new THREE.Vector3(), nrm = new THREE.Vector3(), toCam = new THREE.Vector3();
      // markers move on the compositor (transform), and only when their rounded position actually changed —
      // left/top on 57 chips every frame was relayouting the whole overlay 60x a second
      const put = (mk, x, y, op) => {
        x = Math.round(x * 10) / 10; y = Math.round(y * 10) / 10; op = Math.round(op * 100) / 100;
        if (mk.tx === x && mk.ty === y && mk.op === op) return;
        mk.tx = x; mk.ty = y; mk.op = op;
        mk.el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) translate(-50%,-50%)';
        mk.el.style.opacity = String(op);
      };
      const tick = () => {
        if (!this.isConnected) { this._raf = null; return; }
        this._beat = performance.now();
        this._raf = requestAnimationFrame(tick);
        const w = this.clientWidth, h = this.clientHeight;
        if (!w || !h || this._vis === false) return;
        if (this.canvas.width !== Math.round(w * renderer.getPixelRatio()) || this.canvas.height !== Math.round(h * renderer.getPixelRatio())) {
          renderer.setSize(w, h, false);
          camera.aspect = w / h; camera.updateProjectionMatrix();
          this._dirty = true;
          if (!this._touched) this._fit();
        }
        if (auto && !window.__excal) {          // idle life, but bounded: the selected face never turns away
          sway += 0.0075;
          const amp = 0.3 * Math.max(0, 1 - sway / 5.4);   // greeting sway decays to rest (~12 s), then the loop costs nothing
          theta = (home == null ? 0.35 : home) + Math.sin(sway) * amp;
          if (amp === 0) { auto = false; theta = home == null ? 0.35 : home; }
        }
        const calOn = !!window.__excal;
        if (calOn !== this._calWas) {
          this._calWas = calOn;
          this.canvas.style.cursor = calOn ? 'crosshair' : 'grab';
          this.hint.textContent = calOn ? 'PLACEMENT · TAP THE CARD TO SET THE ARMED NUMBER · DRAG A MARKER TO NUDGE' : 'DRAG TO SPIN · PINCH OR SCROLL TO ZOOM · TAP A NUMBER';
          this.buildLabels();
        }
        if (fly) {
          let dt = fly.theta - theta;
          while (dt > Math.PI) dt -= 2 * Math.PI;
          while (dt < -Math.PI) dt += 2 * Math.PI;
          theta += dt * 0.12; phi += (fly.phi - phi) * 0.12;
          if (Math.abs(dt) < 0.01 && Math.abs(fly.phi - phi) < 0.01) fly = null;
        }
        if (window.__exblock !== this._blk || window.__exstamp !== this._stamp) this.buildLabels();
        const actNow = window.__exactive;
        if (actNow !== this._act) { this._act = actNow; this.paintActive(); }
        if (!this._dirty && pt !== null && Math.abs(theta - pt) < 1e-4 && Math.abs(phi - pp) < 1e-4 && Math.abs(r - pr) < 1e-4) return;
        this._dirty = false; pt = theta; pp = phi; pr = r;
        place();
        this._faceNow = (camera.position.z > 0) === (FZ > 0) ? 'A' : 'B';
        renderer.render(scene, camera);
        let leadOn = false;
        this.markers.forEach((mk) => {
          v.set(mk.p[0], mk.p[1], mk.p[2]);
          nrm.set(0, 0, nz(mk.side));
          toCam.copy(camera.position).sub(v).normalize();
          const facing = nrm.dot(toCam);
          v.project(camera);
          const sx = (v.x * 0.5 + 0.5) * w, sy = (-v.y * 0.5 + 0.5) * h;
          const act = mk.n === this._act;
          const front = facing > 0.12 && v.z < 1;
          if (!front && !act) { if (mk.vis !== 0) { mk.vis = 0; mk.el.style.display = 'none'; } return; }
          if (mk.vis !== 1) { mk.vis = 1; mk.el.style.display = 'flex'; }
          if (front) {
            put(mk, sx, sy, Math.min(1, 0.35 + facing) * (mk.dim && !act ? 0.55 : 1) * (mk.off && !act ? 0.4 : 1));
            return;
          }
          // selected point is on the hidden face: hold it as a callout tied to the anchor
          const dx = sx - w / 2, dy = sy - h / 2, len = Math.hypot(dx, dy) || 1;
          const off = 46;
          const lx = Math.max(18, Math.min(w - 18, sx + dx / len * off)), ly = Math.max(18, Math.min(h - 18, sy + dy / len * off));
          put(mk, lx, ly, 1);
          this.lead.setAttribute('x1', sx); this.lead.setAttribute('y1', sy);
          this.lead.setAttribute('x2', lx); this.lead.setAttribute('y2', ly);
          this.leadDot.setAttribute('cx', sx); this.leadDot.setAttribute('cy', sy);
          leadOn = true;
        });
        const lop = leadOn ? '0.9' : '0';
        if (this._lop !== lop) { this._lop = lop; this.lead.setAttribute('opacity', lop); this.leadDot.setAttribute('opacity', lop); }
      };
      this._tickFn = tick;
      if (window.IntersectionObserver && !this._io) {   // scrolled off the long hub page: stop rendering
        this._io = new IntersectionObserver((es) => {
          const vis = es.some(e => e.isIntersecting);
          if (vis && this._vis === false) { this._dirty = true; this._revive(); }
          this._vis = vis;
        }, { rootMargin: '240px' });
        this._io.observe(this);
      }
      // a mode switch (placement on/off, a new pick) must not wait on the frame loop: the DC calls
      // refresh() and one tick runs synchronously, so cursor, hint, markers and the frame all update
      // even while requestAnimationFrame is throttled (hidden tab, background iframe)
      this.refresh = () => {
        if (!this.isConnected || !this._tickFn) return;
        if (this._raf != null) cancelAnimationFrame(this._raf);
        this._raf = null;
        tick();
      };
      tick();
    }

    buildLabels() {
      if (this._dragN != null) { this.paintActive(); return; }   // never swap the element under a live drag
      const D = window.EX || { blocks: [] };
      const marks = window.__exmarks || {};
      this.markers = [];
      this.labels.innerHTML = '';
      this._blk = window.__exblock;
      this._stamp = window.__exstamp;
      // ALWAYS every marker: showing only the open block made switching tabs look like the marks moved.
      // The open block reads at full strength, the rest sit back as context.
      const cal = !!window.__excal;
      D.blocks.forEach((b, bi) => b.tools.forEach(t => {
        const off = this._blk != null && bi !== this._blk;
        const f = spot(t, marks[t.n]);
        const solid = f.mine || t.est === false;
        const dim = !solid;                                   // an un-placed guess still shows — just lighter, so it reads as a guess
        const n = nz(f.side);
        const p = [n > 0 ? f.mx - W / 2 : W / 2 - f.mx, H / 2 - f.my, n * (T / 2 + 0.2)];
        const el = document.createElement('button');
        el.textContent = String(t.n).padStart(2, '0');
        el.title = t.name;
        el.style.cssText = "position:absolute; left:0; top:0; opacity:0; will-change:transform; pointer-events:auto; cursor:" + (cal ? 'grab' : 'pointer') + "; width:" + (off ? 17 : 26) + "px; height:" + (off ? 17 : 26) + "px; padding:0; border-radius:50%; font-family:'IBM Plex Mono',monospace; font-size:" + (off ? 8 : 10.5) + "px; font-weight:600; display:flex; align-items:center; justify-content:center; border:1.5px " + (solid ? 'solid' : 'dashed') + " rgba(236,237,238,0.8); background:rgba(11,12,14,0.66); color:#ECEDEE; touch-action:none;";
        el.dataset.solid = solid ? '1' : '';
        el.onclick = () => { if (!cal && window.__exselect) window.__exselect(t.n); };
        if (cal) {
          el.onpointerdown = (e) => {
            e.stopPropagation(); e.preventDefault();
            try { el.setPointerCapture(e.pointerId); } catch (err) {}
            this._dragN = t.n;
            if (window.__exselect) window.__exselect(t.n);
          };
          el.onpointermove = (e) => { if (this._dragN === t.n) this._dropAt(e.clientX, e.clientY, t.n, false); };
          el.onpointerup = (e) => { e.stopPropagation(); this._dragN = null; };
          el.onpointercancel = () => { this._dragN = null; };
        }
        this.labels.appendChild(el);
        this.markers.push({ n: t.n, side: f.side, p, el, dim, off });
      }));
      this._dirty = true;
      this.paintActive();
    }

    paintActive() {
      this._dirty = true;
      const act = window.__exactive;
      (this.markers || []).forEach(mk => {
        const on = mk.n === act;
        mk.el.style.borderStyle = mk.el.dataset.solid || on ? 'solid' : 'dashed';
        mk.el.style.borderColor = on ? '#FF6B2C' : 'rgba(236,237,238,0.8)';
        mk.el.style.background = on ? '#FF6B2C' : 'rgba(11,12,14,0.66)';
        mk.el.style.color = on ? '#0B0C0E' : '#ECEDEE';
        mk.el.style.zIndex = on ? '5' : (mk.off ? '1' : '2');   // context never sits on top of the open block
        mk.el.style.width = mk.el.style.height = on ? '30px' : (mk.off ? '17px' : '26px');
      });
      this._act = act;
      const a = (this.markers || []).find(m => m.n === act);
      if (!a || this._dragN != null) return;
      if (this._lastAct === undefined) { this._lastAct = act; if (this._snapTo) this._snapTo(a.side); return; }
      // a NEW pick turns the card to that face; manual spinning afterwards keeps the callout.
      // while placing markers, a pick on the face already in view leaves the camera alone.
      if (this._lastAct !== act) {
        this._lastAct = act;
        if (window.__excal && this._faceNow === a.side) return;
        if (this.flyTo) this.flyTo(a.side);
      }
    }

    disconnectedCallback() { if (this._raf != null) cancelAnimationFrame(this._raf); this._raf = null; clearInterval(this._watchdog); if (this._io) { this._io.disconnect(); this._io = null; } }
  }
  if (!window.customElements.get('ex-viewer')) window.customElements.define('ex-viewer', EXViewer);
})();
